import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './MultipleSelectBox.module.css';

import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../hooks/useSimpleBar';

import {
  Group,
  GroupMember,
  Student,
  StudentGroup,
} from '../../types/classroom';
import { useToggleGroupMutation } from '../../services/student';
import { useSelect } from '../../hooks/useSelect';

import Button from '../Button';

import {
  selectStudentGroupList,
  studentGroupListChanged,
  studentGroupSeqListChanged,
} from '../../features/classroomSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';

interface MultipleSelectBox {
  item: Student | Group | GroupMember;
  list: StudentGroup[];
}

export default function MultipleSelectBox({ item, list }: MultipleSelectBox) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const [simplebarRef] = useSimpleBar();
  const selectRef = useRef<HTMLDivElement>(null);

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [checkedSeqList, setCheckedSeqList] = useState<number[]>(
    item.groupSeqList
  );
  const [checkedGroupList, setCheckedGroupList] = useState<StudentGroup[]>(
    item.groupList.filter(({ seq }: StudentGroup) => {
      return item.groupSeqList.includes(seq);
    })
  );

  const selectedGroupList = useAppSelector(selectStudentGroupList);

  const [toggleGroup] = useToggleGroupMutation();

  const toggleSelect = () => {
    setIsSelectOpen(item.status === 1 && !isSelectOpen);
  };

  const closeSelect = () => {
    setIsSelectOpen(false);
  };

  const selectOption = async (groupSeq: number) => {
    let newGroupSeqList = [];
    try {
      const student = await toggleGroup({ seq: item.seq, groupSeq }).unwrap();
      if (checkedSeqList.includes(groupSeq)) {
        newGroupSeqList = checkedSeqList.filter((list) => list !== groupSeq);
        setCheckedSeqList(newGroupSeqList);
      } else {
        if (checkedSeqList.length < 3) {
          newGroupSeqList = [...checkedSeqList, groupSeq];
          setCheckedSeqList(newGroupSeqList);
        } else {
          newGroupSeqList = [...checkedSeqList.slice(1), groupSeq];
          setCheckedSeqList(newGroupSeqList);
        }
      }
      dispatch(
        studentGroupSeqListChanged({
          seq: item.seq,
          groupSeqList: newGroupSeqList,
        })
      );
      dispatch(studentGroupListChanged(student.groupList));
    } catch (error) {
      console.error('failed to toggle group', error);
    }
  };

  useSelect(selectRef, closeSelect);

  useEffect(() => {
    setCheckedGroupList(
      item.groupList.filter(({ seq }: StudentGroup) =>
        checkedSeqList.includes(seq)
      )
    );
  }, [checkedSeqList]);

  useEffect(() => {
    setCheckedSeqList(item.groupSeqList);
  }, [item]);

  useEffect(() => {
    dispatch(studentGroupListChanged(list));
  }, [list]);

  return (
    <div className={cx('select_wrap')} ref={selectRef}>
      <Button
        name="group"
        isBlindName={true}
        classnames={['select_toggle', isSelectOpen ? 'on' : '']}
        handleClick={toggleSelect}
      >
        <span className={cx('setting_option')}>
          {checkedGroupList.map(({ seq, nm }) => (
            <span key={seq} className={cx('group')} title={nm}>
              {item.status === 1 ? nm : '-'}
            </span>
          ))}
          {!item.groupSeqList.length && '-'}
        </span>
      </Button>

      {isSelectOpen ? (
        <div className={cx('select_menu')}>
          <ul className={cx('select_list')}>
            <SimpleBar forceVisible={true} ref={simplebarRef}>
              {selectedGroupList.map(({ seq, enableYn, nm }) => (
                <li
                  key={seq}
                  className={cx(
                    'items',
                    { checked: checkedSeqList.includes(seq) },
                    {
                      'is--disabled':
                        !enableYn && !checkedSeqList.includes(seq),
                    }
                  )}
                  onClick={() => selectOption(seq)}
                >
                  <span>{nm}</span>
                </li>
              ))}
              {!selectedGroupList.length && (
                <li className={cx('items')}>
                  <span>-</span>
                </li>
              )}
            </SimpleBar>
          </ul>
        </div>
      ) : null}
    </div>
  );
}
